import React from "react";
import { FaFilter, FaTimes, FaSearch, FaCheck } from "react-icons/fa";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { RiEarthFill } from "react-icons/ri";

const MapSidebar = ({
  sidebarOpen,
  toggleSidebar,
  activeTab,
  setActiveTab,
  searchQuery,
  setSearchQuery,
  activeFilters,
  setActiveFilters,
  toggleFilter,
  sectors,
  sectorColors,
  sectorGradients,
  selectedSector,
  setSelectedSector,
  searchedProjects,
  selectedProject,
  setSelectedProject,
  handleMarkerClick,
  resetView,
  sidebarRef,
  isAnimating,
  zoom,
}) => {
  // Sector badge colors
  const sectorBadgeColors = {
    Highways: "bg-blue-900 text-blue-300",
    Commercial: "bg-red-900 text-red-300",
    Infrastructure: "bg-yellow-900 text-yellow-300",
    Construction: "bg-pink-900 text-pink-300",
    Factory: "bg-gray-700 text-gray-300",
    Residential: "bg-emerald-900 text-emerald-300",
    Education: "bg-violet-900 text-violet-300",
    Mall: "bg-orange-900 text-orange-300",
    Railway: "bg-slate-700 text-slate-300",
    Retail: "bg-lime-900 text-lime-300",
    Other: "bg-gray-700 text-gray-300"
  };

  // Dot colors for project list
  const sectorDotColors = {
    Highways: "bg-blue-500",
    Commercial: "bg-red-500",
    Infrastructure: "bg-yellow-500",
    Construction: "bg-pink-500",
    Factory: "bg-black",
    Residential: "bg-emerald-500",
    Education: "bg-violet-500",
    Mall: "bg-orange-500",
    Railway: "bg-slate-500",
    Retail: "bg-lime-500",
    Other: "bg-gray-500"
  };

  return (
    <div
      className={`absolute z-10 top-0 left-0 h-full transition-all duration-300 ease-in-out ${
        sidebarOpen ? "w-80 md:w-96" : "w-0"
      }`}
    >
      {sidebarOpen && (
        <div className="h-full flex flex-col">
          {/* Backdrop with blur and transparency */}
          <div className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-lg backdrop-filter rounded-r-xl border-r border-white border-opacity-10"></div>
          
          {/* Content container with glass effect */}
          <div className="relative h-full flex flex-col">
            {/* Sidebar Header with Gradient */}
            <div className={`p-4 bg-gradient-to-r ${selectedSector ? sectorGradients[selectedSector] : "from-blue-600 to-blue-800"} text-white flex justify-between items-center border-b border-white border-opacity-20`}>
              <div className="flex items-center">
                <RiEarthFill className="text-xl mr-2" />
                <h2 className="text-xl font-bold">Global Projects Map</h2>
              </div>
              <button 
                onClick={toggleSidebar} 
                className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
              >
                <FaTimes />
              </button>
            </div>

            {/* Search & Tabs */}
            <div className="p-4 border-b border-white border-opacity-10">
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-300" />
                </div>
                <input
                  type="text"
                  placeholder="Search projects..."
                  className="block w-full pl-10 pr-3 py-2 border border-white border-opacity-20 rounded-md leading-5 bg-white bg-opacity-10 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white sm:text-sm transition-all duration-200"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="flex border-b border-white border-opacity-10">
                <button
                  className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
                    activeTab === 'projects' 
                      ? 'text-blue-300'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  onClick={() => setActiveTab('projects')}
                >
                  Projects
                  {activeTab === 'projects' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-full"></span>
                  )}
                </button>
                <button
                  className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
                    activeTab === 'filters' 
                      ? 'text-blue-300'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  onClick={() => setActiveTab('filters')}
                >
                  Filters
                  {activeTab === 'filters' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-full"></span>
                  )}
                </button>
              </div>
            </div>

            {/* Filters Tab */}
            {activeTab === 'filters' && (
              <div className="p-4 border-b border-white border-opacity-10 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <div className="flex items-center mb-3">
                  <FaFilter className="mr-2 text-blue-300" />
                  <h3 className="font-semibold text-white">Filter by Sector</h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {sectors.map(sector => (
                    <div key={sector} className="relative">
                      <input
                        type="checkbox"
                        id={`filter-${sector}`}
                        checked={activeFilters.includes(sector)}
                        onChange={() => toggleFilter(sector)}
                        className="absolute opacity-0 h-0 w-0"
                      />
                      <label
                        htmlFor={`filter-${sector}`}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
                          activeFilters.includes(sector)
                            ? `${sectorColors[sector]} border-transparent`
                            : 'border-white border-opacity-20 bg-white bg-opacity-5 hover:bg-opacity-10'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className={`w-4 h-4 rounded-full mr-3 flex items-center justify-center transition-all ${
                            activeFilters.includes(sector) 
                              ? 'bg-white' 
                              : 'border border-white border-opacity-50'
                          }`}>
                            {activeFilters.includes(sector) && (
                              <FaCheck className="text-xs text-gray-800" />
                            )}
                          </span>
                          <span className={`font-medium ${
                            activeFilters.includes(sector) ? 'text-white' : 'text-gray-300'
                          }`}>
                            {sector}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          activeFilters.includes(sector) 
                            ? 'bg-white bg-opacity-20 text-white' 
                            : 'bg-transparent text-gray-300'
                        }`}>
                          {searchedProjects.filter(p => p.projectData.sector === sector).length}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>

                {activeFilters.length > 0 && (
                  <button
                    onClick={() => {
                      setActiveFilters([]);
                      setSelectedSector(null);
                    }}
                    className="mt-4 w-full py-2 px-4 bg-transparent hover:bg-white hover:bg-opacity-10 text-blue-300 rounded-lg flex items-center justify-center transition-colors duration-200 border border-white border-opacity-20"
                  >
                    <FaTimes className="mr-2" /> Clear all filters
                  </button>
                )}
              </div>
            )}

            {/* Project List */}
            <div className="flex-1 overflow-y-auto" ref={sidebarRef} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="p-4">
                {activeTab === 'projects' && (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-white flex items-center">
                        <IoMdInformationCircleOutline className="mr-2 text-blue-300" />
                        {searchQuery 
                          ? `Search Results (${searchedProjects.length})`
                          : activeFilters.length > 0
                            ? `Filtered Projects (${searchedProjects.length})`
                            : `All Projects (${searchedProjects.length})`}
                      </h3>
                      {(searchQuery || activeFilters.length > 0) && (
                        <button
                          onClick={() => {
                            setSearchQuery("");
                            setActiveFilters([]);
                            setSelectedSector(null);
                          }}
                          className="text-xs text-blue-300 hover:text-blue-200 transition-colors duration-200 flex items-center"
                        >
                          <FaTimes className="mr-1" /> Clear
                        </button>
                      )}
                    </div>
                    {searchedProjects.length === 0 ? (
                      <div className="text-center py-8 transition-opacity duration-300">
                        <div className="text-gray-400 mb-2">
                          <FaSearch className="mx-auto text-3xl" />
                        </div>
                        <p className="text-gray-300">
                          {searchQuery 
                            ? "No projects match your search"
                            : activeFilters.length > 0
                              ? "No projects match the selected filters"
                              : "No projects available"}
                        </p>
                      </div>
                    ) : (
                      <ul className="space-y-3">
                        {searchedProjects.map(({ projectData, center, sector }, index) => (
                          <li
                            key={index}
                            className={`rounded-lg cursor-pointer transition-all duration-200 hover:shadow-lg border ${
                              selectedProject?._id === projectData._id
                                ? 'border-blue-300 bg-white bg-opacity-20'
                                : 'border-white border-opacity-20 hover:border-opacity-40 bg-white bg-opacity-10 hover:bg-opacity-15'
                            }`}
                          >
                            <div 
                              className="p-4"
                              onClick={() => {
                                if (selectedProject?._id === projectData._id) {
                                  setSelectedProject(null);
                                  resetView();
                                } else {
                                  handleMarkerClick(center, projectData);
                                }
                              }}
                            >
                              <div className="flex items-start">
                                <div className={`w-4 h-4 rounded-full mt-1 mr-3 flex-shrink-0 ${sectorDotColors[sector] || "bg-transparent"}`}></div>
                                <div className="flex-1 min-w-0">
                                  <h4 
                                    className="font-medium text-white truncate hover:text-blue-300 transition-colors"
                                  >
                                    {projectData.project_name}
                                  </h4>
                                  <div className="flex items-center mt-1">
                                    <span className={`text-xs px-2 py-1 rounded-full ${sectorBadgeColors[sector] || "bg-transparent text-gray-300"} mr-2`}>
                                      {sector}
                                    </span>
                                    {projectData.description && (
                                      <p className="text-xs text-gray-300 truncate">
                                        {projectData.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="ml-2 text-xs text-gray-400 self-center">
                                  {new Date(projectData.updatedAt).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Status Bar */}
            <div className="p-3 bg-black bg-opacity-30 text-xs text-gray-300 border-t border-white border-opacity-10 flex justify-between items-center">
              <div className="flex items-center">
                {isAnimating ? (
                  <span className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-blue-400 mr-1 animate-pulse"></span>
                    Animating...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-green-400 mr-1"></span>
                    {searchedProjects.length} projects
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span>Zoom: {zoom.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapSidebar;

// import React from "react";
// import { FaFilter, FaTimes, FaSearch, FaCheck } from "react-icons/fa";
// import { IoMdInformationCircleOutline } from "react-icons/io";
// import { RiEarthFill } from "react-icons/ri";

// const MapSidebar = ({
//   sidebarOpen,
//   toggleSidebar,
//   activeTab,
//   setActiveTab,
//   searchQuery,
//   setSearchQuery,
//   activeFilters,
//   setActiveFilters,
//   toggleFilter,
//   sectors,
//   sectorColors,
//   sectorGradients,
//   selectedSector,
//   setSelectedSector,
//   searchedProjects,
//   selectedProject,
//   setSelectedProject,
//   handleMarkerClick,
//   resetView,
//   sidebarRef,
//   isAnimating,
//   zoom,
// }) => {
//   // Sector badge colors
//   const sectorBadgeColors = {
//     Highways: "bg-blue-900 text-blue-300",
//     Commercial: "bg-red-900 text-red-300",
//     Infrastructure: "bg-yellow-900 text-yellow-300",
//     Construction: "bg-pink-900 text-pink-300",
//     Factory: "bg-gray-700 text-gray-300",
//     Residential: "bg-emerald-900 text-emerald-300",
//     Education: "bg-violet-900 text-violet-300",
//     Mall: "bg-orange-900 text-orange-300",
//     Railway: "bg-slate-700 text-slate-300",
//     Retail: "bg-lime-900 text-lime-300",
//     Other: "bg-gray-700 text-gray-300"
//   };

//   // Dot colors for project list
//   const sectorDotColors = {
//     Highways: "bg-blue-500",
//     Commercial: "bg-red-500",
//     Infrastructure: "bg-yellow-500",
//     Construction: "bg-pink-500",
//     Factory: "bg-black",
//     Residential: "bg-emerald-500",
//     Education: "bg-violet-500",
//     Mall: "bg-orange-500",
//     Railway: "bg-slate-500",
//     Retail: "bg-lime-500",
//     Other: "bg-gray-500"
//   };

//   return (
//     <div
//       className={`absolute z-10 top-0 left-0 h-full transition-all duration-300 ease-in-out ${
//         sidebarOpen ? "w-80 md:w-96" : "w-0"
//       }`}
//     >
//       {sidebarOpen && (
//         <div className="h-full flex flex-col bg-transparent border-r border-gray-700">
//           {/* Sidebar Header with Gradient */}
//           <div className={`p-4 bg-gradient-to-r ${selectedSector ? sectorGradients[selectedSector] : "from-blue-600 to-blue-800"} text-white flex justify-between items-center border-b border-gray-700`}>
//             <div className="flex items-center">
//               <RiEarthFill className="text-xl mr-2" />
//               <h2 className="text-xl font-bold">Global Projects Map</h2>
//             </div>
//             <button 
//               onClick={toggleSidebar} 
//               className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
//             >
//               <FaTimes />
//             </button>
//           </div>

//           {/* Search & Tabs */}
//           <div className="p-4 border-b border-gray-700">
//             <div className="relative mb-4">
//               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                 <FaSearch className="text-gray-400" />
//               </div>
//               <input
//                 type="text"
//                 placeholder="Search projects..."
//                 className="block w-full pl-10 pr-3 py-2 border border-gray-700 rounded-md leading-5 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200 sm:text-sm transition-all duration-200"
//                 value={searchQuery}
//                 onChange={(e) => setSearchQuery(e.target.value)}
//               />
//             </div>

//             <div className="flex border-b border-gray-700">
//               <button
//                 className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
//                   activeTab === 'projects' 
//                     ? 'text-blue-400'
//                     : 'text-gray-400 hover:text-gray-200'
//                 }`}
//                 onClick={() => setActiveTab('projects')}
//               >
//                 Projects
//                 {activeTab === 'projects' && (
//                   <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></span>
//                 )}
//               </button>
//               <button
//                 className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
//                   activeTab === 'filters' 
//                     ? 'text-blue-400'
//                     : 'text-gray-400 hover:text-gray-200'
//                 }`}
//                 onClick={() => setActiveTab('filters')}
//               >
//                 Filters
//                 {activeTab === 'filters' && (
//                   <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></span>
//                 )}
//               </button>
//             </div>
//           </div>

//           {/* Filters Tab */}
//           {activeTab === 'filters' && (
//             <div className="p-4 border-b border-gray-700 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//               <div className="flex items-center mb-3">
//                 <FaFilter className="mr-2 text-blue-400" />
//                 <h3 className="font-semibold text-gray-200">Filter by Sector</h3>
//               </div>
              
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//                 {sectors.map(sector => (
//                   <div key={sector} className="relative">
//                     <input
//                       type="checkbox"
//                       id={`filter-${sector}`}
//                       checked={activeFilters.includes(sector)}
//                       onChange={() => toggleFilter(sector)}
//                       className="absolute opacity-0 h-0 w-0"
//                     />
//                     <label
//                       htmlFor={`filter-${sector}`}
//                       className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
//                         activeFilters.includes(sector)
//                           ? `${sectorColors[sector]} border-transparent`
//                           : 'border-gray-700 bg-transparent hover:bg-gray-700'
//                       }`}
//                     >
//                       <div className="flex items-center">
//                         <span className={`w-4 h-4 rounded-full mr-3 flex items-center justify-center transition-all ${
//                           activeFilters.includes(sector) 
//                             ? 'bg-white' 
//                             : 'border border-gray-500'
//                         }`}>
//                           {activeFilters.includes(sector) && (
//                             <FaCheck className="text-xs text-gray-800" />
//                           )}
//                         </span>
//                         <span className={`font-medium ${
//                           activeFilters.includes(sector) ? 'text-white' : 'text-gray-300'
//                         }`}>
//                           {sector}
//                         </span>
//                       </div>
//                       <span className={`text-xs px-2 py-1 rounded-full ${
//                         activeFilters.includes(sector) 
//                           ? 'bg-white bg-opacity-20 text-white' 
//                           : 'bg-transparent text-gray-300'
//                       }`}>
//                         {searchedProjects.filter(p => p.projectData.sector === sector).length}
//                       </span>
//                     </label>
//                   </div>
//                 ))}
//               </div>

//               {activeFilters.length > 0 && (
//                 <button
//                   onClick={() => {
//                     setActiveFilters([]);
//                     setSelectedSector(null);
//                   }}
//                   className="mt-4 w-full py-2 px-4 bg-transparent hover:bg-gray-700 text-blue-400 rounded-lg flex items-center justify-center transition-colors duration-200 border border-gray-700"
//                 >
//                   <FaTimes className="mr-2" /> Clear all filters
//                 </button>
//               )}
//             </div>
//           )}

//           {/* Project List */}
//           <div className="flex-1 overflow-y-auto" ref={sidebarRef} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//             <div className="p-4">
//               {activeTab === 'projects' && (
//                 <>
//                   <div className="flex items-center justify-between mb-3">
//                     <h3 className="font-semibold text-gray-300 flex items-center">
//                       <IoMdInformationCircleOutline className="mr-2 text-blue-400" />
//                       {searchQuery 
//                         ? `Search Results (${searchedProjects.length})`
//                         : activeFilters.length > 0
//                           ? `Filtered Projects (${searchedProjects.length})`
//                           : `All Projects (${searchedProjects.length})`}
//                     </h3>
//                     {(searchQuery || activeFilters.length > 0) && (
//                       <button
//                         onClick={() => {
//                           setSearchQuery("");
//                           setActiveFilters([]);
//                           setSelectedSector(null);
//                         }}
//                         className="text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200 flex items-center"
//                       >
//                         <FaTimes className="mr-1" /> Clear
//                       </button>
//                     )}
//                   </div>
//                   {searchedProjects.length === 0 ? (
//                     <div className="text-center py-8 transition-opacity duration-300">
//                       <div className="text-gray-500 mb-2">
//                         <FaSearch className="mx-auto text-3xl" />
//                       </div>
//                       <p className="text-gray-400">
//                         {searchQuery 
//                           ? "No projects match your search"
//                           : activeFilters.length > 0
//                             ? "No projects match the selected filters"
//                             : "No projects available"}
//                       </p>
//                     </div>
//                   ) : (
//                     <ul className="space-y-3">
//                       {searchedProjects.map(({ projectData, center, sector }, index) => (
//                         <li
//                           key={index}
//                           className={`rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md border ${
//                             selectedProject?._id === projectData._id
//                               ? 'border-blue-400 bg-gray-900 opacity-60'
//                               : 'border-gray-700 hover:border-gray-600 bg-gray-900 opacity-25'
//                           }`}
//                         >
//                           <div 
//                             className="p-4"
//                             onClick={() => {
//                               if (selectedProject?._id === projectData._id) {
//                                 setSelectedProject(null);
//                                 resetView();
//                               } else {
//                                 handleMarkerClick(center, projectData);
//                               }
//                             }}
//                           >
//                             <div className="flex items-start">
//                               <div className={`w-4 h-4 rounded-full mt-1 mr-3 flex-shrink-0 ${sectorDotColors[sector] || "bg-transparent"}`}></div>
//                               <div className="flex-1 min-w-0">
//                                 <h4 
//                                   className="font-medium text-white truncate hover:text-blue-400 transition-colors"
//                                 >
//                                   {projectData.project_name}
//                                 </h4>
//                                 <div className="flex items-center mt-1">
//                                   <span className={`text-xs px-2 py-1 rounded-full ${sectorBadgeColors[sector] || "bg-transparent text-gray-300"} mr-2`}>
//                                     {sector}
//                                   </span>
//                                   {projectData.description && (
//                                     <p className="text-xs text-gray-400 truncate">
//                                       {projectData.description}
//                                     </p>
//                                   )}
//                                 </div>
//                               </div>
//                               <div className="ml-2 text-xs text-gray-500 self-center">
//                                 {new Date(projectData.updatedAt).toLocaleDateString()}
//                               </div>
//                             </div>
//                           </div>
//                         </li>
//                       ))}
//                     </ul>
//                   )}
//                 </>
//               )}
//             </div>
//           </div>

//           {/* Status Bar */}
//           <div className="p-3 bg-gray-800 text-xs text-gray-400 border-t border-gray-700 flex justify-between items-center">
//             <div className="flex items-center">
//               {isAnimating ? (
//                 <span className="flex items-center">
//                   <span className="w-2 h-2 rounded-full bg-blue-500 mr-1 animate-pulse"></span>
//                   Animating...
//                 </span>
//               ) : (
//                 <span className="flex items-center">
//                   <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
//                   {searchedProjects.length} projects
//                 </span>
//               )}
//             </div>
//             <div className="flex items-center space-x-2">
//               <span>Zoom: {zoom.toFixed(2)}</span>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };
// export default MapSidebar;
// import React from "react";
// import { FaFilter, FaTimes, FaSearch, FaExpand, FaCompress, FaCheck } from "react-icons/fa";
// import { IoMdInformationCircleOutline } from "react-icons/io";
// import { RiEarthFill } from "react-icons/ri";

// const MapSidebar = ({
//   sidebarOpen,
//   toggleSidebar,
//   activeTab,
//   setActiveTab,
//   searchQuery,
//   setSearchQuery,
//   activeFilters,
//   setActiveFilters,
//   toggleFilter,
//   sectors,
//   sectorColors,
//   sectorGradients,
//   selectedSector,
//   setSelectedSector,
//   searchedProjects,
//   selectedProject,
//   setSelectedProject,
//   handleMarkerClick,
//   resetView,
//   sidebarRef,
//   isAnimating,
//   zoom,
//   isFullscreen,
//   toggleFullscreen
// }) => {
//   return (
//     <div
//       className={`absolute z-10 top-0 left-0 h-full transition-all duration-300 ease-in-out overflow-hidden ${
//         sidebarOpen ? "w-80 md:w-96" : "w-0"
//       }`}
//     >
//       {sidebarOpen && (
//         <div className="h-full flex flex-col bg-gray-900 border-r border-gray-700">
//           {/* Sidebar Header with Gradient */}
//           <div className={`p-4 bg-gradient-to-r ${selectedSector ? sectorGradients[selectedSector] : "from-blue-600 to-blue-800"} text-white flex justify-between items-center border-b border-gray-700`}>
//             <div className="flex items-center">
//               <RiEarthFill className="text-xl mr-2" />
//               <h2 className="text-xl font-bold">Global Projects Map</h2>
//             </div>
//             <button 
//               onClick={toggleSidebar} 
//               className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
//             >
//               <FaTimes />
//             </button>
//           </div>

//           {/* Search & Tabs */}
//           <div className="p-4 border-b border-gray-700">
//             <div className="relative mb-4">
//               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                 <FaSearch className="text-gray-400" />
//               </div>
//               <input
//                 type="text"
//                 placeholder="Search projects..."
//                 className="block w-full pl-10 pr-3 py-2 border border-gray-700 rounded-md leading-5 bg-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200 sm:text-sm transition-all duration-200"
//                 value={searchQuery}
//                 onChange={(e) => setSearchQuery(e.target.value)}
//               />
//             </div>

//             <div className="flex border-b border-gray-700">
//               <button
//                 className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
//                   activeTab === 'projects' 
//                     ? 'text-blue-400'
//                     : 'text-gray-400 hover:text-gray-200'
//                 }`}
//                 onClick={() => setActiveTab('projects')}
//               >
//                 Projects
//                 {activeTab === 'projects' && (
//                   <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></span>
//                 )}
//               </button>
//               <button
//                 className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
//                   activeTab === 'filters' 
//                     ? 'text-blue-400'
//                     : 'text-gray-400 hover:text-gray-200'
//                 }`}
//                 onClick={() => setActiveTab('filters')}
//               >
//                 Filters
//                 {activeTab === 'filters' && (
//                   <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></span>
//                 )}
//               </button>
//             </div>
//           </div>

//           {/* Filters Tab */}
//           {activeTab === 'filters' && (
//             <div className="p-4 border-b border-gray-700 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//               <div className="flex items-center mb-3">
//                 <FaFilter className="mr-2 text-blue-400" />
//                 <h3 className="font-semibold text-gray-200">Filter by Sector</h3>
//               </div>
              
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//                 {sectors.map(sector => (
//                   <div key={sector} className="relative">
//                     <input
//                       type="checkbox"
//                       id={`filter-${sector}`}
//                       checked={activeFilters.includes(sector)}
//                       onChange={() => toggleFilter(sector)}
//                       className="absolute opacity-0 h-0 w-0"
//                     />
//                     <label
//                       htmlFor={`filter-${sector}`}
//                       className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
//                         activeFilters.includes(sector)
//                           ? `${sectorColors[sector]} border-transparent`
//                           : 'border-gray-700 bg-gray-800 hover:bg-gray-700'
//                       }`}
//                     >
//                       <div className="flex items-center">
//                         <span className={`w-4 h-4 rounded-full mr-3 flex items-center justify-center transition-all ${
//                           activeFilters.includes(sector) 
//                             ? 'bg-white' 
//                             : 'border border-gray-500'
//                         }`}>
//                           {activeFilters.includes(sector) && (
//                             <FaCheck className="text-xs text-gray-800" />
//                           )}
//                         </span>
//                         <span className={`font-medium ${
//                           activeFilters.includes(sector) ? 'text-white' : 'text-gray-300'
//                         }`}>
//                           {sector}
//                         </span>
//                       </div>
//                       <span className={`text-xs px-2 py-1 rounded-full ${
//                         activeFilters.includes(sector) 
//                           ? 'bg-white bg-opacity-20 text-white' 
//                           : 'bg-gray-700 text-gray-300'
//                       }`}>
//                         {searchedProjects.filter(p => p.projectData.sector === sector).length}
//                       </span>
//                     </label>
//                   </div>
//                 ))}
//               </div>

//               {activeFilters.length > 0 && (
//                 <button
//                   onClick={() => {
//                     setActiveFilters([]);
//                     setSelectedSector(null);
//                   }}
//                   className="mt-4 w-full py-2 px-4 bg-gray-800 hover:bg-gray-700 text-blue-400 rounded-lg flex items-center justify-center transition-colors duration-200 border border-gray-700"
//                 >
//                   <FaTimes className="mr-2" /> Clear all filters
//                 </button>
//               )}
//             </div>
//           )}

//           {/* Project List */}
//           <div className="flex-1 overflow-y-auto" ref={sidebarRef} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//             <div className="p-4">
//               {activeTab === 'projects' && (
//                 <>
//                   <div className="flex items-center justify-between mb-3">
//                     <h3 className="font-semibold text-gray-300 flex items-center">
//                       <IoMdInformationCircleOutline className="mr-2 text-blue-400" />
//                       {searchQuery 
//                         ? `Search Results (${searchedProjects.length})`
//                         : activeFilters.length > 0
//                           ? `Filtered Projects (${searchedProjects.length})`
//                           : `All Projects (${searchedProjects.length})`}
//                     </h3>
//                     {(searchQuery || activeFilters.length > 0) && (
//                       <button
//                         onClick={() => {
//                           setSearchQuery("");
//                           setActiveFilters([]);
//                           setSelectedSector(null);
//                         }}
//                         className="text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200 flex items-center"
//                       >
//                         <FaTimes className="mr-1" /> Clear
//                       </button>
//                     )}
//                   </div>
//                   {searchedProjects.length === 0 ? (
//                     <div className="text-center py-8 transition-opacity duration-300">
//                       <div className="text-gray-500 mb-2">
//                         <FaSearch className="mx-auto text-3xl" />
//                       </div>
//                       <p className="text-gray-400">
//                         {searchQuery 
//                           ? "No projects match your search"
//                           : activeFilters.length > 0
//                             ? "No projects match the selected filters"
//                             : "No projects available"}
//                       </p>
//                     </div>
//                   ) : (
//                     <ul className="space-y-3">
//                       {searchedProjects.map(({ projectData, center, sector }, index) => (
//                         <li
//                           key={index}
//                           className={`rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md border ${
//                             selectedProject?._id === projectData._id
//                               ? 'border-blue-400 bg-gray-700'
//                               : 'border-gray-700 hover:border-gray-600 bg-gray-800'
//                           }`}
//                         >
//                           <div 
//                             className="p-4"
//                             onClick={() => {
//                               if (selectedProject?._id === projectData._id) {
//                                 setSelectedProject(null);
//                                 resetView();
//                               } else {
//                                 handleMarkerClick(center, projectData);
//                               }
//                             }}
//                           >
//                             <div className="flex items-start">
//                               <div className={`w-4 h-4 rounded-full mt-1 mr-3 flex-shrink-0 ${
//                                 sector === "Highways" ? "bg-blue-500" :
//                                 sector === "Commercial" ? "bg-red-500" :
//                                 sector === "Infrastructure" ? "bg-yellow-500" :
//                                 sector === "Construction" ? "bg-pink-500" :
//                                 sector === "Factory" ? "bg-black" :
//                                 "bg-green-500"
//                               }`}></div>
//                               <div className="flex-1 min-w-0">
//                                 <h4 
//                                   className="font-medium text-white truncate hover:text-blue-400 transition-colors"
//                                 >
//                                   {projectData.project_name}
//                                 </h4>
//                                 <div className="flex items-center mt-1">
//                                   <span className={`text-xs px-2 py-1 rounded-full ${
//                                     sector === "Highways" ? "bg-blue-900 text-blue-300" :
//                                     sector === "Commercial" ? "bg-red-900 text-red-300" :
//                                     sector === "Infrastructure" ? "bg-yellow-900 text-yellow-300" :
//                                     sector === "Construction" ? "bg-pink-900 text-pink-300" :
//                                     sector === "Factory" ? "bg-gray-700 text-gray-300" :
//                                     "bg-green-900 text-green-300"
//                                   } mr-2`}>
//                                     {sector}
//                                   </span>
//                                   {projectData.description && (
//                                     <p className="text-xs text-gray-400 truncate">
//                                       {projectData.description}
//                                     </p>
//                                   )}
//                                 </div>
//                               </div>
//                               <div className="ml-2 text-xs text-gray-500 self-center">
//                                 {new Date(projectData.updatedAt).toLocaleDateString()}
//                               </div>
//                             </div>
//                           </div>
//                         </li>
//                       ))}
//                     </ul>
//                   )}
//                 </>
//               )}
//             </div>
//           </div>

//           {/* Status Bar */}
//           <div className="p-3 bg-gray-800 text-xs text-gray-400 border-t border-gray-700 flex justify-between items-center">
//             <div className="flex items-center">
//               {isAnimating ? (
//                 <span className="flex items-center">
//                   <span className="w-2 h-2 rounded-full bg-blue-500 mr-1 animate-pulse"></span>
//                   Animating...
//                 </span>
//               ) : (
//                 <span className="flex items-center">
//                   <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
//                   {searchedProjects.length} projects
//                 </span>
//               )}
//             </div>
//             <div className="flex items-center space-x-2">
//               <span>Zoom: {zoom.toFixed(2)}</span>
//               {/* <span className="w-px h-4 bg-gray-600"></span> */}
//               {/* <button 
//                 onClick={toggleFullscreen}
//                 className="text-gray-400 hover:text-gray-300 transition-colors"
//                 title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
//               >
//                 {isFullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
//               </button> */}
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default MapSidebar;